import type { TTableCellElement, TTableElement } from 'platejs';
import type { SlateElementProps } from 'platejs/static';

import { BaseTablePlugin } from '@platejs/table';
import { SlateElement } from 'platejs/static';
import * as React from 'react';
import { cn } from '../../../utils/cn';

export function TableElementStatic({
  children,
  ...props
}: SlateElementProps<TTableElement>) {
  const { disableMarginLeft } = props.editor.getOptions(BaseTablePlugin);
  const marginLeft = disableMarginLeft ? 0 : props.element.marginLeft;

  return (
    <SlateElement
      {...props}
      className="overflow-x-auto"
      style={{ paddingLeft: marginLeft }}
    >
      <div className="group/table relative w-full plate-viewer-table-wrapper">
        <table
          className="mr-0 table h-px w-full border-collapse plate-viewer-table"
        >
          <tbody className="min-w-full plate-viewer-tbody">{children}</tbody>
        </table>
      </div>
    </SlateElement>
  );
}

export function TableRowElementStatic(props: SlateElementProps) {
  return (
    <SlateElement {...props} as="tr" className="h-full plate-viewer-tr">
      {props.children}
    </SlateElement>
  );
}

export function TableCellElementStatic({
  isHeader,
  ...props
}: SlateElementProps<TTableCellElement> & {
  isHeader?: boolean;
}) {
  const { editor, element } = props;
  const { api } = editor.getPlugin(BaseTablePlugin);

  const { minHeight, width } = api.table.getCellSize({ element });
  const borders = api.table.getCellBorders({ element });

  return (
    <SlateElement
      {...props}
      as={isHeader ? 'th' : 'td'}
      className={cn(
        'h-full overflow-visible border-none p-0',
        element.background ? 'bg-(--cellBackground)' : 'bg-transparent',
        isHeader && 'text-left font-normal *:m-0',
        'before:size-full',
        'before:absolute before:box-border before:select-none before:content-[\'\']',
        isHeader ? 'plate-viewer-th' : 'plate-viewer-td',
        borders
        && cn(
          borders.bottom?.size && 'before:border-b before:border-b-c600',
          borders.right?.size && 'before:border-r before:border-r-c600',
          borders.left?.size && 'before:border-l before:border-l-c600',
          borders.top?.size && 'before:border-t before:border-t-c600',
        ),
      )}
      style={
        {
          '--cellBackground': element.background,
          'maxWidth': width || 240,
          'minWidth': width || 120,
        } as React.CSSProperties
      }
      attributes={{
        ...props.attributes,
        colSpan: api.table.getColSpan(element),
        rowSpan: api.table.getRowSpan(element),
      }}
    >
      <div
        className="relative z-20 box-border h-full px-4 py-1"
        style={{ minHeight }}
      >
        {props.children}
      </div>
    </SlateElement>
  );
}

export function TableCellHeaderElementStatic(
  props: SlateElementProps<TTableCellElement>,
) {
  return <TableCellElementStatic {...props} isHeader />;
}
